import { useEffect, useRef, useState } from "react";
import {
  RemoteParticipant,
  LocalParticipant,
  VideoTrack,
  LocalVideoTrack,
} from "twilio-video";
import styled from "styled-components";
import { useRoom } from "../../hooks/useRoom";
import { useRemoteParticipant } from "../../hooks/useRemoteParticipant";
import SharedScreen from "./SharedScreen";
import useParticipantTracks from "../../hooks/useParticipantTracks";
import ParticipantVideo from "./ParticipantVideo";
import ParticipantAudio from "./ParticipantAudio";

const ParticipantWrapper = styled("div")`
  width: 100%;
  height: fit-content;
  display: ${(props) => (props.hidden ? "none" : "flex")};
  flex-flow: column;
  gap: 4px;
  position: relative;
`;

interface IParticipantProps {
  participant: RemoteParticipant | LocalParticipant;
  local: boolean;
  localScreenTrack?: LocalVideoTrack;
}
const Participant = ({
  participant,
  local,
  localScreenTrack,
}: IParticipantProps): React.ReactElement => {
  const { cameraTrack, screenTrack, audioTrack, isFrontFacing } =
    useParticipantTracks(participant, local);
  const { currentFocused, handleChangeFocused, handleFocusedVideoOff } =
    useRoom();
  const { localRemoteVideoOff, localRemoteAudioOff } = useRemoteParticipant();

  const [remoteVideoOff, setRemoteVideoOff] = useState<boolean>(false);
  const [remoteAudioOff, setRemoteAudioOff] = useState<boolean>(false);
  const [remoteSharing, setRemoteSharing] = useState<boolean>(false);

  // Refs para os elementos do HTML ao qual será anexado o audio e o video no DOM
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>();
  const screenSharingRef = useRef<HTMLVideoElement>(null);

  // Adiciona track da camera ao seu respectivo elemento no DOM
  useEffect(() => {
    console.log("****** Add track de video *******");
    console.log("videoRef:", videoRef);
    console.log("cameraTrack:", cameraTrack);
    console.log("*********************************");
    if (videoRef && videoRef.current) {
      if (cameraTrack) {
        cameraTrack.attach(videoRef.current);
        if (videoRef.current.paused) videoRef.current.play();
        setRemoteVideoOff(!cameraTrack.isEnabled);
      }
    }

    return () => {
      if (cameraTrack) {
        cameraTrack.removeAllListeners();
        if (videoRef && videoRef.current) {
          console.log("****** Remove track de video *******:");
          cameraTrack.detach(videoRef.current);
        }
      }
    };
  }, [cameraTrack, videoRef?.current]);

  // Adiciona track da tela compartilhada ao seu respectivo elemento no DOM
  useEffect(() => {
    console.log("****** Add track de tela compartilhada *******");
    console.log("screenSharingRef:", screenSharingRef);
    console.log("screenTrack:", screenTrack);
    console.log("*********************************");
    if (screenSharingRef && screenSharingRef.current) {
      if (screenTrack) {
        setRemoteSharing(true);
        screenTrack.attach(screenSharingRef.current);
        if (screenSharingRef.current.paused) screenSharingRef.current.play();
        handleChangeFocused("screen", screenTrack);
      } else {
        if (currentFocused === "screen") {
          handleChangeFocused("remote", cameraTrack || null);
        }
        setRemoteSharing(false);
      }
    }

    return () => {
      if (screenSharingRef && screenSharingRef.current) {
        if (screenTrack) {
          console.log("****** Remove track de tela compartilhada *******:");
          screenTrack.detach(screenSharingRef.current);
          if (currentFocused === "screen") {
            handleChangeFocused("remote", cameraTrack || null);
          }
        }
      }
    };
  }, [screenTrack, cameraTrack]);

  // Adiciona tracks de audio aos seus respectivos elementos no DOM
  useEffect(() => {
    console.log("****** Add track de audio *******");
    console.log("audioRef:", audioRef);
    console.log("audioTrack:", audioTrack);
    console.log("*********************************");
    if (!audioTrack || local) return;

    audioRef.current = audioTrack.attach();
    audioRef.current.setAttribute('data-cy-audio-track-name', audioTrack.name);
    audioRef.current.setAttribute('autoplay', 'true');
    document.body.appendChild(audioRef.current);

    if (audioRef.current.paused) audioRef.current.play();
    audioRef.current.volume = 1.0;

    // Define listeners para quando o track de audio for habilitado/desabilitado
    // realiza o attach/detach do elemento de audio no DOM novamente para garantir que o audio funcione
    audioTrack.on("disabled", async (track) => {
      console.log("audioTrack disabled", track);
      await track.detach(audioRef.current);
      setRemoteAudioOff(true);
    });
    audioTrack.on("enabled", async (track) => {
      console.log("audioTrack enabled", track);
      await track.detach(audioRef.current);
      await track.attach(audioRef.current);
      setRemoteAudioOff(false);
    });

    setRemoteAudioOff(!audioTrack.isEnabled);

    return () => {
      if (audioTrack && audioRef.current) {
        console.log("****** Unmount | Deixa de ouvir tracks de audio *******:");
        audioTrack.removeAllListeners();
      }
    };
  }, [audioTrack]);

  // Atualiza estado do audio remoto mutado/desmutado para o participante local
  useEffect(() => {
    if (audioRef && audioRef.current) {
      audioRef.current.muted = localRemoteAudioOff;
    }
  }, [localRemoteAudioOff]);

  // Define listeners para quando o track de video for habilitado/desabilitado
  useEffect(() => {
    // Lida com estado do video focado
    const handleVideoFocused = (track: VideoTrack) => {
      // Atualiza estado do video remoto
      console.log("****** Video track changed *******:");
      console.log("track:", track);
      console.log("*********************************");
      setRemoteVideoOff(!track.isEnabled);
      if (currentFocused === "remote" && !local && !localRemoteVideoOff) {
        handleFocusedVideoOff(!track.isEnabled);
      }
    };

    if (cameraTrack) {
      if (videoRef.current?.paused) videoRef.current.play();
      cameraTrack.on("disabled", handleVideoFocused);
      cameraTrack.on("enabled", handleVideoFocused);
    }

    return () => {
      if (cameraTrack) {
        console.log("****** Unmount | deixa de ouvir camera *******:");
        cameraTrack.off("disabled", handleVideoFocused);
        cameraTrack.off("enabled", handleVideoFocused);
      }
    };
  }, [cameraTrack, cameraTrack?.isEnabled, currentFocused, videoRef?.current, localRemoteVideoOff]);

  // Adiciona o track local da tela compartilhada ao seu respectivo elemento no DOM
  useEffect(() => {
    console.log("****** Add track de tela compartilhada local *******");
    console.log("screenSharingRef:", screenSharingRef);
    console.log("localScreenTrack:", localScreenTrack);
    console.log("*********************************");
    if (screenSharingRef && screenSharingRef.current) {
      if (localScreenTrack) {
        setRemoteSharing(true);
        localScreenTrack.attach(screenSharingRef.current);
        if (screenSharingRef.current.paused) screenSharingRef.current.play();
      }
    }

    return () => {
      if (localScreenTrack) {
        console.log("****** Remove track de tela compartilhada local *******:");
        localScreenTrack.detach();
        setRemoteSharing(false);
      }
    };
  }, [localScreenTrack]);

  // Verifica qual video está focado e esconde os outros
  const handleCurrentFocused = (): boolean => {
    return (
      (local && currentFocused === "local") ||
      (!local && currentFocused === "remote")
    );
  };

  return (
    <ParticipantWrapper hidden={handleCurrentFocused() && !remoteSharing}>
      {/* Camera e informações do participante */}
      <ParticipantVideo
        ref={videoRef}
        isLocalParticipant={local}
        isVideoOff={(localRemoteVideoOff && !local) || remoteVideoOff}
        isAudioOff={remoteAudioOff}
        isFrontFacing={isFrontFacing}
        participant={participant}
        cameraTrack={cameraTrack}
        hide={handleCurrentFocused()}
      />
      {/* Audio participante remoto */}
      {/* <ParticipantAudio ref={audioRef} isLocalParticipant={local} /> */}
      {/* Tela compartilhada */}
      <SharedScreen
        remoteSharing={remoteSharing}
        screenSharingRef={screenSharingRef}
        isLocalParticipant={local}
        screenTrack={screenTrack}
        localScreenTrack={localScreenTrack}
        participantName={
          participant.identity?.split("-").slice(1, -1).join("-") || "Participante"
        }
      />
    </ParticipantWrapper>
  );
};

export default Participant;
